import { Assert, Branded } from '@cotera/utilities';

export type ResourceInfo =
  | {
      kind: 'llm';
      modelProvider: string;
      modelAlias: string;
      /**
       * The ai-sdk reports a single prompt-token figure inclusive of cache
       * read/write. Billing splits it so each bucket is priced separately
       * (see `credit-math.ts`):
       *
       *   - `uncached-input-tokens`     full price
       *   - `cache-read-input-tokens`   cheap (cache hit)
       *   - `cache-write-input-tokens`  slight premium (cache creation)
       *
       * `input-tokens` is the legacy aggregate kind: it is no longer emitted
       * by new code and exists only so historical rows still decode (priced
       * at the old full rate).
       *
       * DEPRECATED[input-tokens] (COT-6097) — the `'input-tokens'` literal
       * below is a legacy aggregate kind. Retained ONLY so historical /
       * un-metered `input-tokens` rows still decode and price at the old full
       * rate. New code emits `uncached-input-tokens` + the two cache kinds
       * instead. Remove the literal (and every other DEPRECATED[input-tokens]
       * site) once no `input-tokens` rows remain in `atc_resource_usage`.
       * (Not a TS `@deprecated` tag on purpose: that would flag the whole
       * `usageKind` field, not just the one legacy member.)
       */
      usageKind: // DEPRECATED[input-tokens] (COT-6097): legacy, decode-only — see above.
      | 'input-tokens'
        | 'uncached-input-tokens'
        | 'cache-read-input-tokens'
        | 'cache-write-input-tokens'
        | 'output-tokens'
        | 'api-call';
    }
  | {
      kind: 'toolcall';
      toolAlias: string;
    }
  | {
      kind: 'agent' | 'program';
    };

/**
 * Formal Spec:
 *
 * One of:
 * ```typescript
 * `llm::${modelProvider}::${modelAlias}::${'input-tokens' | 'uncached-input-tokens' | 'cache-read-input-tokens' | 'cache-write-input-tokens' | 'output-tokens' | 'api-call'}`
 *
 * Note: `input-tokens` is DEPRECATED[input-tokens] (COT-6097) — legacy
 * aggregate kind, decode-only; new code never emits it.
 * `toolcall::${toolAlias}`
 * `agent`
 * `program`
 * ```
 */
export type ResourceKey = Branded<string, 'resource-key'>;

const derive = (info: ResourceInfo): ResourceKey => {
  switch (info.kind) {
    case 'llm': {
      const { modelProvider, modelAlias, usageKind } = info;
      return `llm::${modelProvider}::${modelAlias}::${usageKind}` as ResourceKey;
    }

    case 'toolcall': {
      const { toolAlias } = info;
      return `toolcall::${toolAlias}` as ResourceKey;
    }

    case 'agent':
      return `agent` as ResourceKey;
    case 'program':
      return `program` as ResourceKey;

    default:
      return Assert.unreachable(info);
  }
};

/**
 * Raises an error if key is not properly formatted
 */
const decode = (key: ResourceKey): ResourceInfo => {
  const parts = key.split('::');

  const kind = Assert.assertExists(parts[0]);

  Assert.assert(
    kind === 'llm' ||
      kind === 'toolcall' ||
      kind === 'agent' ||
      kind === 'program'
  );

  if (kind === 'agent') {
    Assert.assert(parts.length === 1);
    return { kind: 'agent' };
  } else if (kind === 'program') {
    Assert.assert(parts.length === 1);
    return { kind: 'program' };
  } else if (kind === 'llm') {
    Assert.assert(parts.length === 4);
    const modelProvider = Assert.assertExists(parts[1]);
    const modelAlias = Assert.assertExists(parts[2]);
    const usageKind = Assert.assertExists(parts[3]);
    Assert.assert(
      // DEPRECATED[input-tokens] (COT-6097): accepted for legacy/historical
      // rows only — new code never produces this kind. Drop this clause once
      // no `input-tokens` rows remain in `atc_resource_usage`.
      usageKind === 'input-tokens' ||
        usageKind === 'uncached-input-tokens' ||
        usageKind === 'cache-read-input-tokens' ||
        usageKind === 'cache-write-input-tokens' ||
        usageKind === 'output-tokens' ||
        usageKind === 'api-call'
    );

    return {
      kind,
      modelProvider,
      modelAlias,
      usageKind,
    };
  } else if (kind === 'toolcall') {
    Assert.assert(parts.length === 2);
    const toolAlias = Assert.assertExists(parts[1]);
    return { kind, toolAlias };
  } else {
    return Assert.unreachable(kind);
  }
};

export const ResourceUsageKeyHelpers = {
  derive,
  decode,
};
